/**
 * Error state component for API failures.
 */

import type { ApiRequestError } from '../lib/api';

interface ErrorStateProps {
  error: ApiRequestError | null;
  onRetry?: () => void;
}

export function ErrorState({ error, onRetry }: ErrorStateProps) {
  // Handle specific error codes
  const isForbidden = error?.status === 403;
  const isUnauthorized = error?.status === 401;
  const isNetworkError = error?.code === 'NETWORK_ERROR';

  const getErrorContent = () => {
    if (isUnauthorized) {
      return {
        title: 'Session expired',
        description: 'Please sign in again to continue.',
        action: 'Sign In',
        onAction: () => window.location.reload(),
      };
    }

    if (isForbidden) {
      return {
        title: 'Access denied',
        description: 'You don\'t have permission to view these tasks.',
        action: null,
        onAction: null,
      };
    }

    if (isNetworkError) {
      return {
        title: 'No connection',
        description: 'Check your internet connection and try again.',
        action: 'Retry',
        onAction: onRetry,
      };
    }

    return {
      title: 'Something went wrong',
      description: error?.message ?? 'An unexpected error occurred.',
      action: 'Retry',
      onAction: onRetry,
    };
  };

  const content = getErrorContent();

  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      {/* Error icon */}
      <div className="w-16 h-16 mb-6 rounded-2xl bg-red-500/10 flex items-center justify-center">
        <svg
          className="w-8 h-8 text-red-400"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M12 7v6M12 16v.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <h3 className="text-lg font-semibold text-ink mb-1">{content.title}</h3>
      <p className="text-sm text-ink-muted max-w-xs mb-6">{content.description}</p>

      {content.action && content.onAction && (
        <button
          type="button"
          onClick={content.onAction}
          className="
            px-5 py-2.5 rounded-lg font-medium text-sm
            bg-accent text-white
            hover:bg-accent-muted active:scale-[0.98]
            transition-all duration-150
            focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface
          "
        >
          {content.action}
        </button>
      )}

      {/* Error code for debugging */}
      {error?.code && (
        <p className="mt-4 text-2xs text-ink-subtle font-mono">
          Error: {error.code}
        </p>
      )}
    </div>
  );
}
